import { useState, useEffect } from 'react';
import { Eye, Trash2, Plus, Edit2, ArrowLeft, BarChart2, CheckSquare } from 'lucide-react';

const TestOfDayManager = () => {
  const [assignedTests, setAssignedTests] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });
  const [testToRemove, setTestToRemove] = useState(null);
  
  // Form states
  const [testId, setTestId] = useState('');
  const [testDetails, setTestDetails] = useState(null);
  const [customTitle, setCustomTitle] = useState('');
  const [useDefaultTitle, setUseDefaultTitle] = useState(true);
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [editingTest, setEditingTest] = useState(null);
  const [loadingTests, setLoadingTests] = useState(false);
  
  const formatForDateTimeInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const parseTestDate = (dateVal) => {
    if (!dateVal) return -Infinity;
    const timestamp = new Date(dateVal).getTime();
    return isNaN(timestamp) ? -Infinity : timestamp;
  };

  const sortByTestDateDesc = (a, b) => {
    const timeA = parseTestDate(a.test_date);
    const timeB = parseTestDate(b.test_date);
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    return (b.id || 0) - (a.id || 0);
  };
  
  const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

  // Fetch assigned tests (Test of the Day)
  const fetchAssignedTests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all tests of the day
      const response = await fetch(`${API_BASE_URL}/test-of-the-day`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch tests');
      }
      
      const data = await response.json();
      const sortedData = Array.isArray(data) ? [...data].sort(sortByTestDateDesc) : [];
      
      // Set the data first to show the list quicker
      setAssignedTests(sortedData);
      
      // In the background, fetch test details to enhance the display
      const fetchDetails = async () => {
        try {
          const testsWithDetails = await Promise.all(
            sortedData.map(async (test) => {
              try {
                const detailsResponse = await fetch(`${API_BASE_URL}/tests/${test.test_id}`);
                if (detailsResponse.ok) {
                  const testDetails = await detailsResponse.json();
                  return {
                    ...test,
                    testName: testDetails.name
                  };
                }
                return test;
              } catch (error) {
                console.error(`Error fetching details for test ID ${test.test_id}:`, error);
                return test;
              }
            })
          );
          
          setAssignedTests(testsWithDetails.sort(sortByTestDateDesc));
        } catch (error) {
          console.error("Error fetching test details:", error);
        }
      };
      
      // Start fetching details without blocking
      fetchDetails();
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch assigned tests");
      setLoading(false);
      showModalMessage("Error", "Failed to fetch tests. Please try again later.");
    }
  };

  // Fetch available tests
  const fetchAvailableTests = async () => {
    try {
      setLoadingTests(true);
      
      // Get all tests from test-type-assignments endpoint
      const testAssignmentsResponse = await fetch(`${API_BASE_URL}/test-type-assignments/type/TEST_OF_THE_DAY`);
      
      if (!testAssignmentsResponse.ok) {
        throw new Error('Failed to fetch test assignments');
      }
      
      const testAssignmentsData = await testAssignmentsResponse.json();
      console.log("Test assignments data:", testAssignmentsData);
      
      // Get currently assigned tests from test-of-the-day endpoint
      const assignedTestsResponse = await fetch(`${API_BASE_URL}/test-of-the-day`);
      
      if (!assignedTestsResponse.ok) {
        throw new Error('Failed to fetch assigned tests');
      }
      
      const assignedTestsData = await assignedTestsResponse.json();
      const assignedTestIds = assignedTestsData.map(test => test.test_id);
      console.log("Currently assigned test IDs:", assignedTestIds);
      
      // Filter out tests that are already assigned as Test of the Day
      const availableTestIds = testAssignmentsData
        .filter(test => !assignedTestIds.includes(test.test_id))
        .map(test => test.test_id);
      console.log("Available test IDs:", availableTestIds);
      
      // Show loading placeholders
      setAvailableTests(availableTestIds.map(id => ({ 
        id, 
        test_id: id, 
        name: `Loading test ${id}...` 
      })));
      
      // Fetch details for each available test
      const availableTestsWithDetails = await Promise.all(
        availableTestIds.map(async (testId) => {
          try {
            const response = await fetch(`${API_BASE_URL}/tests/${testId}`);
            
            if (response.ok) {
              const testData = await response.json();
              return {
                id: testData.test_id,
                test_id: testData.test_id,
                name: testData.name
              };
            } else {
              console.error(`Failed to fetch details for test ID ${testId}`);
              return {
                id: testId,
                test_id: testId,
                name: `Test ${testId}`
              };
            }
          } catch (error) {
            console.error(`Error fetching test ID ${testId}:`, error);
            return {
              id: testId,
              test_id: testId,
              name: `Test ${testId}`
            };
          }
        })
      );
      
      setAvailableTests(availableTestsWithDetails);
      setLoadingTests(false);
    } catch (err) {
      console.error("Error in fetchAvailableTests:", err);
      showModalMessage("Error", "Failed to fetch available tests. Please try again later.");
      setLoadingTests(false);
    }
  };

  // Fetch test details when a test is selected
  const fetchTestDetails = async (selectedTestId) => {
    if (!selectedTestId) {
      setTestDetails(null);
      setCustomTitle('');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/tests/${selectedTestId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch test details');
      }
      
      const data = await response.json();
      setTestDetails(data);
      setCustomTitle(data.name); // Initialize custom title with test name
    } catch (err) {
      showModalMessage("Error", "Failed to fetch test details. Please try again.");
      setTestDetails(null);
    }
  };

  // Handle test selection change
  const handleTestSelection = (e) => {
    const selected = e.target.value;
    setTestId(selected);
    if (selected) {
      fetchTestDetails(selected);
    } else {
      setTestDetails(null);
    }
  };

  // Assign a test as Test of the Day
  const assignTest = async (e) => {
    if (e) e.preventDefault();
    
    try {
      // Validate form
      if (!testId) {
        showModalMessage("Validation Error", "Please select a test.");
        return;
      }
      
      // Determine which title to use
      const titleToUse = useDefaultTitle ? (testDetails?.name || '') : customTitle;
      
      if (!titleToUse) {
        showModalMessage("Validation Error", "Title cannot be empty.");
        return;
      }
      
      if (startTime && endTime && new Date(endTime) <= new Date(startTime)) {
        showModalMessage("Validation Error", "End time must be after start time.");
        return;
      }

      // Create test of the day assignment
      const newAssignment = {
        test_id: parseInt(testId),
        title: titleToUse,
        test_date: testDate,
        start_time: startTime ? new Date(startTime).toISOString() : null,
        end_time: endTime ? new Date(endTime).toISOString() : null
      };
      
      const response = await fetch(`${API_BASE_URL}/test-of-the-day`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAssignment)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create test of the day');
      }
      
      const data = await response.json();
      
      setAssignedTests([data, ...assignedTests].sort(sortByTestDateDesc));
      resetForm();
      setShowAssignModal(false);
      showModalMessage("Success", `Test "${titleToUse}" has been assigned as Test of the Day.`);
      
      // Refresh the lists
      fetchAssignedTests();
      fetchAvailableTests();
    } catch (err) {
      showModalMessage("Error", "Failed to assign test. Please try again.");
    }
  };

  // Initialize removal process
  const initiateRemoval = (id) => {
    setTestToRemove(id);
    setShowConfirmModal(true);
  };

  // Confirm and remove a test assignment
  const confirmRemoval = async () => {
    if (!testToRemove) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/test-of-the-day/${testToRemove}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete test of the day');
      }
      
      setAssignedTests(assignedTests.filter(test => test.id !== testToRemove));
      showModalMessage("Success", "Test assignment removed successfully.");
      setShowConfirmModal(false);
      setTestToRemove(null);
      
      // Refresh lists
      fetchAssignedTests();
      fetchAvailableTests();
    } catch (err) {
      showModalMessage("Error", "Failed to remove test assignment. Please try again.");
      setShowConfirmModal(false);
      setTestToRemove(null);
    }
  };

  // Initialize edit mode
  const initializeEdit = (test) => {
    setEditingTest(test);
    setTestId(test.test_id.toString());
    setCustomTitle(test.title);
    setUseDefaultTitle(false);
    setTestDate(test.test_date || new Date().toISOString().split('T')[0]);
    setStartTime(formatForDateTimeInput(test.start_time));
    setEndTime(formatForDateTimeInput(test.end_time));
    fetchTestDetails(test.test_id);
    setShowEditModal(true);
  };
  
  // Update an existing test assignment
  const updateTest = async (e) => {
    if (e) e.preventDefault();
    
    try {
      if (!editingTest) return;
      
      // Determine which title to use
      const titleToUse = useDefaultTitle ? (testDetails?.name || '') : customTitle;
      
      if (!titleToUse) {
        showModalMessage("Validation Error", "Title cannot be empty.");
        return;
      }

      if (startTime && endTime && new Date(endTime) <= new Date(startTime)) {
        showModalMessage("Validation Error", "End time must be after start time.");
        return;
      }
      
      // Update test of the day assignment
      const updatedAssignment = {
        title: titleToUse,
        test_date: testDate,
        start_time: startTime ? new Date(startTime).toISOString() : null,
        end_time: endTime ? new Date(endTime).toISOString() : null
      };
      
      const response = await fetch(`${API_BASE_URL}/test-of-the-day/${editingTest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedAssignment)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update test of the day');
      }
      
      const data = await response.json();
      
      // Update local state
      setAssignedTests(assignedTests.map(test => 
        test.id === editingTest.id ? data : test
      ).sort(sortByTestDateDesc));
      
      resetForm();
      setShowEditModal(false);
      showModalMessage("Success", `Test "${titleToUse}" has been updated.`);
      
      // Refresh assigned tests
      fetchAssignedTests();
    } catch (err) {
      showModalMessage("Error", "Failed to update test. Please try again.");
    }
  };
  
  // Reset form fields
  const resetForm = () => {
    setTestId('');
    setTestDetails(null);
    setCustomTitle('');
    setUseDefaultTitle(true);
    setTestDate(new Date().toISOString().split('T')[0]);
    setStartTime('');
    setEndTime('');
    setEditingTest(null);
  };

  // Show modal with message
  const showModalMessage = (title, message) => {
    setModalContent({ title, message });
    setShowMessageModal(true);
  };

  // Format date for display
  const formatDate = (dateString, shortFormat = false) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    if (shortFormat) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  // Load tests on component mount
  useEffect(() => {
    fetchAssignedTests();
    fetchAvailableTests();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Test of the Day</h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading tests...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Test of the Day</h2>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
        <button 
          onClick={() => window.history.back()}
          className="mt-4 px-4 py-2 rounded-md bg-teal-500 text-white text-sm inline-flex items-center gap-2"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header section */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Test of the Day</h1>
            <p className="text-gray-600 mt-1">Manage daily tests for students</p>
          </div>
          <button
            className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-md flex items-center"
            onClick={() => {
              resetForm();
              setShowAssignModal(true);
            }}
          >
            <Plus size={18} className="mr-1" /> Assign Test
          </button>
        </div>
        
        {/* Assigned Tests Section */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold mb-4">Assigned Tests ({assignedTests.length})</h2>
          
          {assignedTests.length === 0 ? (
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 rounded-full bg-gray-200 text-gray-400">
                  <CheckSquare size={24} />
                </div>
              </div>
              <p className="text-gray-600 mb-4">No tests have been assigned as Test of the Day yet.</p>
              <button
                onClick={() => {
                  resetForm();
                  setShowAssignModal(true);
                }}
                className="px-4 py-2 rounded-md bg-teal-500 text-white text-sm inline-flex items-center gap-2"
              >
                <Plus size={16} /> Assign Your First Test
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S.No
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Test ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Test Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assignedTests.map((test, index) => (
                    <tr key={test.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{test.title}</div>
                        {test.testName && test.title !== test.testName && (
                          <div className="text-xs text-gray-500 italic">Original: {test.testName}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {test.test_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(test.test_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(test.created_at, true)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => window.open(`/tests/${test.test_id}`, '_blank')}
                            title="View Test"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            onClick={() => window.open(`/test-of-day-results/${test.id}`, '_blank')}
                            title="View Results"
                          >
                            <BarChart2 size={16} />
                          </button>
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => initializeEdit(test)}
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => initiateRemoval(test.id)}
                            title="Remove"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Assign Test Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Assign Test of the Day</h3>
            
            <form onSubmit={assignTest}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="testId">
                  Select Test
                </label>
                <select
                  id="testId"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={testId}
                  onChange={handleTestSelection}
                  required
                  disabled={loadingTests}
                >
                  <option value="">-- Select a test --</option>
                  {loadingTests ? (
                    <option value="" disabled>Loading tests...</option>
                  ) : (
                    availableTests.map((test) => (
                      <option key={test.id} value={test.test_id}>
                        {test.name} (ID: {test.test_id})
                      </option>
                    ))
                  )}
                </select>
              </div>
              
              {testDetails && (
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <input
                      id="useDefaultTitle"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={useDefaultTitle}
                      onChange={() => setUseDefaultTitle(!useDefaultTitle)}
                    />
                    <label className="ml-2 block text-gray-700 text-sm font-medium" htmlFor="useDefaultTitle">
                      Use default test name
                    </label>
                  </div>
                  
                  {!useDefaultTitle && (
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="customTitle">
                        Custom Title
                      </label>
                      <input
                        id="customTitle"
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        required
                      />
                    </div>
                  )}
                  
                  {useDefaultTitle && (
                    <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                      Title: <span className="font-medium">{testDetails.name}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="testDate">
                  Test Date
                </label>
                <input
                  id="testDate"
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={testDate}
                  onChange={(e) => setTestDate(e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Start Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    End Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2 text-[11px] text-gray-500">
                  If set, exact times override test date availability.
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => setShowAssignModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={!testId || loadingTests}
                >
                  Assign Test
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">{modalContent.title}</h3>
            <p className="text-gray-700 mb-4">{modalContent.message}</p>
            <div className="flex justify-end">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={() => setShowMessageModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Test Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Edit Test of the Day</h3>
            
            <form onSubmit={updateTest}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Test ID
                </label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                  {testId} {testDetails && `(${testDetails.name})`}
                </div>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <input
                    id="editUseDefaultTitle"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={useDefaultTitle}
                    onChange={() => setUseDefaultTitle(!useDefaultTitle)}
                  />
                  <label className="ml-2 block text-gray-700 text-sm font-medium" htmlFor="editUseDefaultTitle">
                    Use default test name
                  </label>
                </div>
                
                {!useDefaultTitle && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="editCustomTitle">
                      Title
                    </label>
                    <input
                      id="editCustomTitle"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      required
                    />
                  </div>
                )}
                
                {useDefaultTitle && testDetails && (
                  <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                    Title: <span className="font-medium">{testDetails.name}</span>
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="editTestDate">
                  Test Date
                </label>
                <input
                  id="editTestDate"
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={testDate}
                  onChange={(e) => setTestDate(e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Start Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    End Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2 text-[11px] text-gray-500">
                  If set, exact times override test date availability.
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => {
                    resetForm();
                    setShowEditModal(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update Test
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Confirm Removal</h3>
            <p className="text-gray-700 mb-4">Are you sure you want to remove this test assignment?</p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={confirmRemoval}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestOfDayManager;